'use client';
import Link from 'next/link';
import { Box, Typography, Button, Card, CardContent, CardActions, Chip, Container, Link as MuiLink, Stack, Grid } from '@mui/material';
import SvgIcon, { type SvgIconProps } from '@mui/material/SvgIcon';
import PlatformCarousel from '@/components/PlatformCarousel';
import { MarketingFeatureRow } from '@/components/marketing/MarketingFeatureRow';
import { MarketingHeroVisual } from '@/components/marketing/MarketingHeroVisual';
import { ROICalculator } from '@/letsgo/templates/ROICalculator';
import { UserCount } from '@/letsgo/templates/UserCount';
import { PlatformSelector } from '@/letsgo/templates/PlatformSelector';
import { marketingPrimaryCtaSx } from '@/lib/marketingButtonStyles';

/** Phone-framed mobile captures — clear single product shot per feature section */
const FRAME = '/images/marketing/mobile/framed';
const SPOT = '/images/marketing/spotlights';

function CheckCircleOutlineIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-1 14.5l-4.5-4.5 1.4-1.4L11 13.7l6.1-6.1 1.4 1.4L11 16.5z" />
    </SvgIcon>
  );
}

const PLANS = [
  {
    title: 'Free',
    price: '$0/month',
    features: [
      '3 social accounts',
      'Basic analytics',
      'Community access',
      '2 free Academy courses',
      'Basic template marketplace access',
    ],
    color: 'primary',
    cta: 'Start for Free',
  },
  {
    title: 'Creator',
    price: '$19/month',
    annualPrice: '$189/year',
    features: [
      '3 social accounts',
      'Advanced analytics',
      'Basic scheduling',
      '5 Academy courses',
      'Full template marketplace access',
      'Email support',
    ],
    color: 'info',
    cta: 'Upgrade to Creator',
  },
  {
    title: 'Pro',
    price: '$49/month',
    annualPrice: '$489/year',
    features: [
      'All social accounts',
      'Advanced analytics & insights',
      'Advanced scheduling',
      'All Academy courses',
      'Premium template marketplace',
      'Early access to new features',
      'Priority support',
    ],
    color: 'secondary',
    cta: 'Start Pro free trial',
    highlight: true,
  },
  {
    title: 'Enterprise',
    price: 'Contact Us',
    features: [
      'Custom integrations',
      'Onboarding support',
      'Dedicated account manager',
      'White-glove migrations',
    ],
    color: 'success',
    cta: 'Contact Sales',
  },
];

export default function Home() {
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        fontFamily: 'system-ui, sans-serif',
        bgcolor: 'background.default',
      }}
    >
        {/* Hero — headline + lifestyle photo with floating Create UI (Hootsuite-style) */}
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid
            container
            spacing={4}
            alignItems="center"
            sx={{ pt: { xs: 4, md: 8 }, pb: { xs: 3, md: 6 } }}
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                component="h1"
                sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 700, mb: 2, lineHeight: 1.15 }}
              >
                The AI platform designed for real creators
              </Typography>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.2rem' }, mb: 2.5, color: 'text.secondary', lineHeight: 1.6 }}>
                Create, schedule, and publish from your phone — one studio for every network you use.
              </Typography>
              <Button
                component={Link}
                href="/auth/signup"
                variant="contained"
                color="primary"
                sx={marketingPrimaryCtaSx({
                  fontSize: '1.1rem',
                  padding: '11px 28px',
                  textDecoration: 'none',
                })}
              >
                Get Started Free
              </Button>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
              <MarketingHeroVisual
                spotlightSrc={`${SPOT}/create-spotlight.png`}
                spotlightAlt="FloAI Create — draft a post with title, editor, and publish actions"
              />
            </Grid>
          </Grid>
        </Container>

        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
          <Stack
            component="section"
            spacing={3}
            alignItems="stretch"
            sx={{ width: '100%' }}
            aria-labelledby="home-platform-carousel-heading"
          >
            <Typography
              id="home-platform-carousel-heading"
              component="h2"
              variant="h6"
              align="center"
              fontWeight={600}
              color="text.primary"
              sx={{ width: '100%', flexShrink: 0 }}
            >
              Publish to all your favorite platforms instantly
            </Typography>
            <Box sx={{ width: '100%', minWidth: 0, flex: '0 0 auto' }}>
              <PlatformCarousel />
            </Box>
          </Stack>
        </Container>

        <MarketingFeatureRow
          headline="Create on the go"
          body="Draft posts, attach media, and use AI tools from a mobile-first Create hub — built for creators who work from their phone."
          featureLabel="FloAI Create features:"
          featureBullets={[
            'Multi-network account picker',
            'Write / Preview composer',
            'AI tools and media attachments',
            'Save, schedule, or publish',
          ]}
          imageSrc={`${FRAME}/create-mobile-framed.png`}
          imageAlt="FloAI Create on mobile — composer and connected accounts"
          imageRight
          linkHref="/getting-started"
          linkLabel="Getting started"
        />

        <MarketingFeatureRow
          headline="Your media library, anywhere"
          body="Upload JPEGs and video once, then reuse them when you publish to Instagram, YouTube, TikTok, and more."
          featureLabel="FloAI Media Library features:"
          featureBullets={[
            'Upload JPEG and MP4 once',
            'Grid view on mobile',
            'Reuse in Create and publish flows',
          ]}
          imageSrc={`${FRAME}/media-mobile-framed.png`}
          imageAlt="FloAI Media Library on mobile"
          imageRight={false}
          linkHref="/features"
          linkLabel="See features"
        />

        <MarketingFeatureRow
          headline="Schedule in one tap"
          body="Pick a date and time, choose native delivery or Buffer, and add content to your calendar without leaving the app."
          featureLabel="FloAI scheduling features:"
          featureBullets={[
            'Quick picks: today, tomorrow, this week',
            'Native delivery or Buffer queue',
            'Recurrence and preview',
          ]}
          imageSrc={`${FRAME}/schedule-mobile-framed.png`}
          imageAlt="FloAI schedule flow on mobile"
          imageRight
        />

        <MarketingFeatureRow
          headline="Connect 15+ networks"
          body="Link Instagram, YouTube, LinkedIn, X, and more from Profile — then publish from one place when you're ready."
          featureLabel="FloAI integrations:"
          featureBullets={[
            'Connect Instagram, YouTube, LinkedIn, X',
            'OAuth from Profile',
            'One studio for every network',
          ]}
          imageSrc={`${FRAME}/profile-mobile-framed.png`}
          imageAlt="FloAI Profile with connected social accounts"
          imageRight={false}
          linkHref="/developers"
          linkLabel="Integrations"
        />

        <MarketingFeatureRow
          headline="One inbox for comments"
          body="Sync connected networks to see Instagram, Facebook, X mentions, and more in a unified inbox — read-only monitoring built in."
          featureLabel="FloAI Inbox features:"
          featureBullets={[
            'Unified feed across networks',
            'Filter by source',
            'Read-only monitoring for App Review',
          ]}
          imageSrc={`${FRAME}/inbox-mobile-framed.png`}
          imageAlt="FloAI inbox on mobile"
          imageRight
        />

        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <Box id="roi-calculator" sx={{ width: '100%', minWidth: 0 }}>
            <ROICalculator />
          </Box>
        </Container>

        <UserCount />

        <PlatformSelector />

        <Container
          maxWidth="lg"
          sx={{
            px: { xs: 2, sm: 3 },
            py: 4,
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Stack spacing={2} alignItems="center" sx={{ width: '100%', mb: 3, textAlign: 'center' }}>
            <Typography component="h2" variant="h2" fontWeight={700} color="text.primary" sx={{ width: '100%' }}>
              Choose Your Plan
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ width: '100%', maxWidth: 640, px: { xs: 0, sm: 1 } }}>
              Quick snapshot of popular tiers. For annual pricing, white-label, and the full feature comparison table, see{' '}
              <MuiLink component={Link} href="/pricing" fontWeight={600}>
                full pricing
              </MuiLink>
              .
            </Typography>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              width: '100%',
              gap: 4,
              alignItems: 'stretch',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            }}
          >
            {PLANS.map((plan, /* _ */) => (
                <Card key={plan.title} sx={{ p: 2, borderRadius: 3, boxShadow: 2, minHeight: 280, width: '100%', maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography fontWeight={700} variant="subtitle1" mb={0.5} color="text.primary">
                      {plan.title}
                      {plan.highlight && <Chip label="Most Popular" size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="h4" mb={2} color="text.primary">{plan.price}</Typography>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {plan.features.map((f) => (
                        <li key={f} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}><CheckCircleOutlineIcon color="primary" sx={{ mr: 1, fontSize: '1.1rem' }} /><Typography fontSize={14} color="text.secondary">{f}</Typography></li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardActions sx={plan.title === 'Pro' ? { mt: 1, p: 1, width: '100%' } : {}}>
                    {plan.title === 'Pro' ? (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        component={Link}
                        href="/pricing"
                        sx={marketingPrimaryCtaSx({
                          fontWeight: 800,
                          fontSize: '1.25rem',
                          lineHeight: 1,
                          borderRadius: 1,
                        })}
                      >
                        <Box component="span" sx={{ color: 'inherit' }}>
                          {plan.cta}
                        </Box>
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        component={Link}
                        href="/pricing"
                        sx={(theme) => ({
                          mt: 1,
                          ...(theme.palette.mode === 'light' && {
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: 'action.hover',
                              borderColor: 'text.secondary',
                            },
                          }),
                        })}
                      >
                        {plan.cta}
                      </Button>
                    )}
                  </CardActions>
                </Card>
            ))}
          </Box>
        </Container>
        {/* Footer with Quick Links dropdown */}
        {/* Footer rendered inside ClientHeaderFooter */}
    </Box>
  );
}
